import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { skills, categories } from "../data/skills";

const Skills: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Fonction pour obtenir l'image correspondante à une compétence
  const getSkillImage = (skillName: string) => {
    const imageMap: { [key: string]: string } = {
      React: "/img/reactjs.png",
      NextJS: "/img/nextjs.png",
      NodeJS: "/img/nodejs.png",
      NestJS: "/img/nestjs.png",
      ExpressJS: "/img/express-js.png",
      TypeScript: "/img/typescript.png",
      JavaScript: "/img/javascript.png",
      CSS: "/img/css.png",
      HTML: "/img/html.png",
      TailwindCSS: "/img/tailwindcss.png",
      ShadCN: "/img/shadcn.png",
      GitLab: "/img/gitlab.png",
      Git: "/img/github.png",
      Docker: "/img/docker.png",
      WordPress: "/img/wordpress.png",
      Figma: "/img/figma.png",
      Canva: "/img/canva.png",
      MySQL: "/img/mysql.png",
      PostgreSQL: "/img/postgres.png",
      MongoDB: "/img/mongo.png",
      Airtable: "/img/airtable.png",
      Elementor: "/img/elementor.png",
      PHP: "/img/php.png",
    };

    return imageMap[skillName] || null;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const categoryVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const skillVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <section
      id="skills"
      className="py-20 bg-gradient-to-br from-secondary-50 via-white to-blue-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-blue-900/20 relative overflow-hidden"
    >
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-10 right-10 w-32 h-32 bg-primary-200 dark:bg-primary-600 rounded-full opacity-20 animate-float"></div>
        <div
          className="absolute bottom-10 left-10 w-40 h-40 bg-blue-200 dark:bg-blue-600 rounded-full opacity-20 animate-float"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={categoryVariants} className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 bg-gradient-to-r from-gray-900 via-primary-600 to-blue-600 dark:from-white dark:via-primary-400 dark:to-blue-400 bg-clip-text text-transparent">
              Compétences
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-blue-600 dark:from-primary-400 dark:to-blue-400 mx-auto rounded-full mb-8"></div>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Mes technologies et outils de développement
            </p>
          </motion.div>

          {/* Skills Grid */}
          <div className="space-y-12">
            {categories.map((category) => {
              const categorySkills = skills.filter(
                (skill) => skill.category === category.id
              );

              return (
                <motion.div key={category.id} variants={categoryVariants}>
                  <div className="mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {category.name}
                      </h3>
                    </div>
                    <div
                      className={`w-32 h-1 bg-gradient-to-r ${category.color} mx-auto rounded-full`}
                    ></div>
                  </div>

                  <div className="flex flex-wrap justify-center gap-6">
                    {categorySkills.map((skill, index) => {
                      const skillImage = getSkillImage(skill.name);

                      return (
                        <motion.div
                          key={skill.name}
                          variants={skillVariants}
                          whileHover={{
                            y: -8,
                            scale: 1.05,
                            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                          }}
                          transition={{ type: "spring", stiffness: 300 }}
                          className="group relative w-full max-w-[140px]"
                        >
                          <div className="bg-white/80 dark:bg-secondary-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-soft dark:shadow-dark-soft border border-white/20 dark:border-secondary-700/20 hover:border-primary-200 dark:hover:border-primary-600/50 transition-all duration-300 flex flex-col items-center justify-center min-h-[120px] w-full">
                            {/* Logo */}
                            <div className="mb-3 group-hover:scale-110 transition-transform duration-300">
                              {skillImage ? (
                                <img
                                  src={skillImage}
                                  alt={skill.name}
                                  className="w-12 h-12 object-contain"
                                />
                              ) : (
                                <div className="text-4xl">{}</div>
                              )}
                            </div>

                            {/* Name */}
                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm text-center">
                              {skill.name}
                            </h4>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Call to action */}
          <motion.div variants={categoryVariants} className="text-center mt-16">
            <motion.a
              href="/CV/CV-JPB.pdf"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-800 dark:to-gray-600 text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 shadow-soft dark:shadow-dark-soft hover:shadow-glow dark:hover:shadow-dark-glow"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Voir mon CV complet
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
